/* eslint-disable camelcase */
/* eslint-disable consistent-return */
/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable no-nested-ternary */
import React, { CSSProperties } from 'react';
import { flushSync } from 'react-dom';
import Select from 'react-select';
import { difference, sortBy } from 'lodash';
import { Form } from '@fulcrumapp/fulcrum-core';
import { AsyncComponent, AsyncProps, AsyncState } from '../shared/async-component';
import Dialog from '../shared/dialog';
import Input from '../shared/input';
import Alert from '../components/alert';
import Loader from '../shared/loader';
import Label from '../shared/label';
import ErrorHandler from '../shared/error-handler';
import Button from '../shared/button';
import PermissionsEditor from '../shared/permissions-editor';
import { Membership, Resource } from '../models/member';
import Group, { GroupsInheritedPermissions, transformGroup } from '../models/group';
import { styles } from '../shared/simple-select';
import { removeSpaces, emailTest } from '../utils/methods';
import { AppContext } from '../hooks/use-app';
import splitPermissionRequest, { PermissionObject } from './utils/split-permission-request';

type QueryFormResults = { name: string, id: string, status: string }[];

interface Parameters {
  membershipID: string;
}

interface Props extends AsyncProps {
  show: boolean;
  onSubmit: () => void;
  onCancel: () => void;
}

interface MemberData {
  forms: PermissionObject | null;
  layers: PermissionObject | null;
  projects: PermissionObject | null;
  groups: PermissionObject | null;
}

interface State extends AsyncState {
  firstName: string;
  lastName: string;
  email: string;
  emailError: string;
  role: string;
  roles: any[];
  isManaged: boolean;
  isEditable: boolean;
  membership: Membership | null;
  forms: Option[];
  projects: Option[];
  layers: Option[];
  groups: GroupOption[];
  groupsInheritedPermissions: GroupsInheritedPermissions;
  initialGroupsInheritedPermissions: GroupsInheritedPermissions;
  initialForms: string[];
  initialProjects: string[];
  initialLayers: string[];
  initialGroups: string[];
  selectedForms: string[];
  selectedProjects: string[];
  selectedLayers: string[];
  selectedGroups: string[];
  showFormPermissions: boolean;
  showProjectPermissions: boolean;
  showLayerPermissions: boolean;
  showGroupPermissions: boolean;
  loadingType?: string;
  formsManagedViaGroup: string[];
}

interface Option {
  id: string;
  name: string;
}

interface GroupOption extends Option {
  isManaged: boolean;
}

interface PermissionButton {
  type: Resource;
  label: string;
  title: string;
  selected: number;
  total: number;
  disabled: boolean;
  show: boolean;
  objects: any[];
  selectedIDs: string[];
  preselectedObjects?: Record<string, string[]>;
}

const rows = '4';
const initialGroupsInheritedPermissions = Group().getSelectedGroupsPermissions();

const gridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 2fr',
  gridTemplateRows: `repeat(${rows}, 1fr)`,
  gridColumnGap: 15,
  gridRowGap: 24,
  alignItems: 'center',
  marginBottom: 15,
  marginTop: 15,
  transition: 'opacity .25s',
};

const DEFAULT_STATE = {
  membership: null,
  firstName: '',
  lastName: '',
  email: '',
  emailError: '',
  role: '',
  roles: [],
  forms: [],
  projects: [],
  layers: [],
  groups: [],
  groupsInheritedPermissions: initialGroupsInheritedPermissions,
  initialGroupsInheritedPermissions,
  initialForms: [],
  initialProjects: [],
  initialLayers: [],
  initialGroups: [],
  selectedForms: [],
  selectedProjects: [],
  selectedLayers: [],
  selectedGroups: [],
  isManaged: false,
  isEditable: true,
  showFormPermissions: false,
  showProjectPermissions: false,
  showLayerPermissions: false,
  showGroupPermissions: false,
  loadingType: undefined,
  formsManagedViaGroup: [],
};

export default class MembersEdit extends AsyncComponent<Parameters, Props, State> {
  state: Readonly<State> = {
    ...AsyncComponent.DEFAULT_STATE,
    ...DEFAULT_STATE,
  };

  managedStatus = 'This user is managed by an external app. Name and email cannot be edited.';
  multiOrgStatus = 'This user belongs to multiple organizations. Name and email cannot be edited.';
  editRoleStatus = 'This user has a role that cannot be edited. All fields are disabled.';
  editUserStatus = 'You cannot update your own membership information.';
  groupsText = 'Adding Members to a Group gives them immediate access to its Apps, Map Layers, and Projects.';
  individualText = 'Provide access for Apps, Map Layers, and Projects.';
  individualTextGroups = 'Additionally, you can provide access for Apps, Map Layers, and Projects that aren\'t part of a Group.';
  emailError = 'Email contains invalid characters or formatting.';

  static contextType = AppContext;
  GROUP = Group();
  mappedRoles: { label: string, value: string }[] = [];

  handleEmailBlur = () => {
    const isValidEmail = emailTest(this.state.email);
    if (!isValidEmail) {
      this.setState({ emailError: this.emailError });
    }
  };

  async load() {
    const membership: Membership = await this.fetchMembership();

    const [
      selectedForms,
      selectedProjects,
      selectedLayers,
      selectedGroups,
      allForms,
      allProjects,
      allLayers,
      allGroups,
      allRoles,
    ] = await Promise.all([
      this.fetchPermissions('member_forms'),
      this.fetchPermissions('member_projects'),
      this.fetchPermissions('member_layers'),
      this.fetchPermissions('member_groups'),
      this.fetchAllForms(),
      this.fetchAllProjects(),
      this.fetchAllLayers(),
      this.fetchAllGroups(),
      this.fetchRoles(),
    ]);

    const ids = (items: any[]) => items.map((item) => item.id);
    const formsManagedViaGroup = (forms: any[]) => forms.filter(({ from_group }) => from_group).map(({ id }) => id);

    flushSync(() => {
      this.setState({
        membership,
        firstName: membership.first_name,
        lastName: membership.last_name,
        email: membership.email,
        emailError: '',
        role: membership.role_id,
        roles: allRoles,
        forms: allForms as Form[],
        layers: allLayers,
        projects: allProjects,
        groups: allGroups,
        initialForms: ids(selectedForms),
        initialProjects: ids(selectedProjects),
        initialLayers: ids(selectedLayers),
        initialGroups: ids(selectedGroups),
        selectedForms: ids(selectedForms),
        selectedProjects: ids(selectedProjects),
        selectedLayers: ids(selectedLayers),
        selectedGroups: ids(selectedGroups),
        isManaged: membership.is_managed,
        isEditable: membership.is_editable,
        formsManagedViaGroup: formsManagedViaGroup(selectedForms),
      });
    });

    this.mappedRoles = allRoles.map((item) => ({ label: item.name, value: item.id }));
    this.setGroupsInheritedPermissions({ allGroups, selectedGroups: ids(selectedGroups) }, true);
  }

  async reset() {
    this.setState(DEFAULT_STATE);
  }

  async fetchPermissions(type: string) {
    return (await this.context.api.permissions.all({
      object_id: this.parameters.membershipID,
      type,
    })).objects;
  }

  fetchMembership() {
    return this.context.api.memberships.find(this.parameters.membershipID, { editing_info: 1 });
  }

  async fetchRoles() {
    const { objects } = await this.context.api.roles.all();

    return sortBy(objects, 'name');
  }

  async fetchAllForms() {
    try {
      const queryFormResults: QueryFormResults = (await this.context.api.query.run({
        q: 'SELECT form_id AS id, name, status FROM forms WHERE system_type IS NULL ORDER BY name, status;',
      })).objects;

      const forms = queryFormResults.map((form) => {
        const displayName = form.status === 'inactive' ? `${form.name} (inactive)` : form.name;

        return {
          id: form.id,
          name: displayName,
        };
      });

      return forms;
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  async fetchAllProjects() {
    return (await this.context.api.query.run({
      q: 'SELECT project_id AS id, name FROM projects ORDER BY name',
    })).objects;
  }

  async fetchAllLayers() {
    return (await this.context.api.layers.all({ per_page: 1500 })).objects;
  }

  async fetchAllGroups() {
    const { objects } = await this.context.api.groups.all({ associations: 'true' });

    const transformedObjects = objects.map((item: any) => transformGroup(item));

    return sortBy(transformedObjects, 'name');
  }

  get canEditUserProfile() {
    return !this.state.isManaged && this.state.isEditable && this.canEditRole;
  }

  get selectedRole() {
    return this.state.roles.find((role) => role.id === this.state.role);
  }

  get isOwnerRole() {
    return this.selectedRole && this.selectedRole.can_manage_roles;
  }

  get isManagerRoleUser() {
    return (this.context.user.id === this.state.membership?.user_id) && !this.isOwnerRole;
  }

  get canEditRole() {
    return this.selectedRole != null && !this.isManagerRoleUser;
  }

  get canSubmitMember() {
    return this.state.firstName && this.state.lastName && this.state.email && !this.state.emailError;
  }

  async updateMemberData({
    forms, projects, layers, groups,
  }: MemberData) {
    await this.execute(async () => {
      const userAttributes = this.canEditUserProfile ? {
        first_name: this.state.firstName,
        last_name: this.state.lastName,
        email: this.state.email,
      } : {};

      this.setState({ loadingType: 'Info' });
      await this.context.api.memberships.update(this.parameters.membershipID, {
        ...userAttributes,
        role_id: this.state.role,
      });

      if (forms) {
        this.setState({ loadingType: 'Forms' });
        const formChanges = splitPermissionRequest(forms);
        await Promise.allSettled(
          formChanges.map((obj) => this.context.api.memberships.changePermissions(obj)),
        );
      }

      if (projects) {
        this.setState({ loadingType: 'Projects' });
        const projectChanges = splitPermissionRequest(projects);
        await Promise.allSettled(
          projectChanges.map((obj) => this.context.api.memberships.changePermissions(obj)),
        );
      }

      if (layers) {
        this.setState({ loadingType: 'Layers' });
        const projectChanges = splitPermissionRequest(layers);
        await Promise.allSettled(
          projectChanges.map((obj) => this.context.api.memberships.changePermissions(obj)),
        );
      }

      if (groups) {
        this.setState({ loadingType: 'Groups' });
        const groupChanges = splitPermissionRequest(groups);
        await Promise.allSettled(
          groupChanges.map((obj) => this.context.api.memberships.changePermissions(obj)),
        );
      }

      this.setState({ loadingType: undefined });
    });

    if (!this.state.hasError) {
      this.props.onSubmit();
    }
  }

  handleSubmitGroup = () => {
    const {
      inheritedFormIds,
      inheritedLayerIds,
      inheritedProjectIds,
    } = this.state.groupsInheritedPermissions;

    const {
      inheritedFormIds: initialInheritedFormIds,
      inheritedLayerIds: initialInheritedLayerIds,
      inheritedProjectIds: initialInheritedProjectIds,
    } = this.state.initialGroupsInheritedPermissions;

    const nonManagedInitialForms = this.state.initialForms.filter(
      (resource) => !initialInheritedFormIds.includes(resource),
    );

    const nonManagedInitialLayers = this.state.initialLayers.filter(
      (resource) => !initialInheritedLayerIds.includes(resource),
    );

    const nonManagedInitialProjects = this.state.initialProjects.filter(
      (resource) => !initialInheritedProjectIds.includes(resource),
    );

    const nonManagedForms = this.state.selectedForms.filter(
      (formID) => !inheritedFormIds.includes(formID),
    );

    const nonManagedProjects = this.state.selectedProjects.filter(
      (projectID) => !inheritedProjectIds.includes(projectID),
    );

    const nonManagedLayers = this.state.selectedLayers.filter(
      (layerID) => !inheritedLayerIds.includes(layerID),
    );

    return {
      nonManagedForms,
      nonManagedLayers,
      nonManagedProjects,
      nonManagedInitialForms,
      nonManagedInitialLayers,
      nonManagedInitialProjects,
    };
  };

  handleSubmit = () => {
    const isValidEmail = emailTest(this.state.email);
    if (!isValidEmail) {
      this.setState({ emailError: this.emailError });
      return;
    }

    const {
      nonManagedForms, nonManagedLayers, nonManagedProjects,
      nonManagedInitialForms, nonManagedInitialLayers, nonManagedInitialProjects,
    } = this.handleSubmitGroup();

    const formChanges = {
      add: difference(nonManagedForms, nonManagedInitialForms),
      remove: difference(nonManagedInitialForms, nonManagedForms),
      type: 'member_forms',
      membership_id: this.parameters.membershipID,
    };

    const projectChanges = {
      add: difference(nonManagedProjects, nonManagedInitialProjects),
      remove: difference(nonManagedInitialProjects, nonManagedProjects),
      type: 'member_projects',
      membership_id: this.parameters.membershipID,
    };

    const layerChanges = {
      add: difference(nonManagedLayers, nonManagedInitialLayers),
      remove: difference(nonManagedInitialLayers, nonManagedLayers),
      type: 'member_layers',
      membership_id: this.parameters.membershipID,
    };

    const groupChanges = {
      add: difference(this.state.selectedGroups, this.state.initialGroups),
      remove: difference(this.state.initialGroups, this.state.selectedGroups),
      type: 'member_groups',
      membership_id: this.parameters.membershipID,
    };

    this.updateMemberData({
      forms: formChanges.add.length || formChanges.remove.length ? formChanges : null,
      projects: projectChanges.add.length || projectChanges.remove.length ? projectChanges : null,
      layers: layerChanges.add.length || layerChanges.remove.length ? layerChanges : null,
      groups: groupChanges.add.length || groupChanges.remove.length ? groupChanges : null,
    })
      // make sure to catch any error
      .catch(console.error);
  };

  handleCancel = () => {
    this.props.onCancel();
  };

  handlePermissionsCancel = (type: Resource) => (selectedValues: string[]) => {
    this.updatePermissions(type, selectedValues);
  };

  handlePermissionsDone = (type: Resource) => (selectedValues: string[]) => {
    this.updatePermissions(type, selectedValues);
  };

  handleFirstNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ firstName: event.target.value });
  };

  handleLastNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ lastName: event.target.value });
  };

  handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const email = event.target.value;
    this.setState({
      email,
      emailError: '',
      hasError: false,
    });
  };

  handleRoleChange = (item: { value: string }) => {
    this.setState({ role: item ? item.value : this.state.role });
  };

  setGroupsInheritedPermissions({
    allGroups = this.state.groups,
    selectedGroups = this.state.selectedGroups,
  }, init?: boolean) {
    const { selectedForms, selectedLayers, selectedProjects } = this.state;
    const groupsInheritedPermissions = this.GROUP.getSelectedGroupsPermissions({
      allGroups, selectedGroups,
    });
    const { inheritedFormIds, inheritedLayerIds, inheritedProjectIds } = groupsInheritedPermissions;

    const currentState = {
      groupsInheritedPermissions,
      selectedForms: selectedForms.filter((id) => !inheritedFormIds.includes(id)),
      selectedProjects: selectedProjects.filter((id) => !inheritedProjectIds.includes(id)),
      selectedLayers: selectedLayers.filter((id) => !inheritedLayerIds.includes(id)),
    };

    if (init) {
      this.setState({
        ...currentState,
        initialGroupsInheritedPermissions: groupsInheritedPermissions,
      });
    } else {
      this.setState(currentState);
    }
  }

  updatePermissions = (type: Resource, values: string[]) => {
    switch (type) {
      case 'layers':
        this.setState({ selectedLayers: values, showLayerPermissions: false });
        break;
      case 'projects':
        this.setState({ selectedProjects: values, showProjectPermissions: false });
        break;
      case 'forms':
        this.setState({ selectedForms: values, showFormPermissions: false });
        break;
      case 'groups': {
        this.setState({ selectedGroups: values, showGroupPermissions: false });
        this.setGroupsInheritedPermissions({ selectedGroups: values });
        break;
      }
      default:
        break;
    }
  };

  showPermissionModal = (type: Resource) => () => {
    this.setState({
      showFormPermissions: type === 'forms',
      showProjectPermissions: type === 'projects',
      showLayerPermissions: type === 'layers',
      showGroupPermissions: type === 'groups',
    });
  };

  renderPermissions(type: Resource) {
    let button: PermissionButton;
    const {
      inheritedForms, inheritedLayers, inheritedProjects,
      inheritedFormIds, inheritedProjectIds, inheritedLayerIds,
    } = this.state.groupsInheritedPermissions;

    switch (type) {
      case 'layers': {
        const { layers, selectedLayers } = this.state;

        button = {
          type: Resource.Layers,
          label: 'Layers',
          title: layers.length > 0 ? 'Select Layers' : 'No Layers',
          selected: selectedLayers.length + inheritedLayerIds.length,
          total: layers.length,
          objects: layers,
          selectedIDs: selectedLayers,
          preselectedObjects: inheritedLayers,
          show: this.state.showLayerPermissions,
          disabled: !this.canEditRole || this.isOwnerRole || layers.length === 0,
        };

        break;
      }
      case 'projects': {
        const { projects, selectedProjects } = this.state;

        button = {
          type: Resource.Projects,
          label: 'Projects',
          title: projects.length > 0 ? 'Select Projects' : 'No Projects',
          selected: selectedProjects.length + inheritedProjectIds.length,
          total: projects.length,
          objects: projects,
          selectedIDs: selectedProjects,
          preselectedObjects: inheritedProjects,
          show: this.state.showProjectPermissions,
          disabled: !this.canEditRole || this.isOwnerRole || projects.length === 0,
        };

        break;
      }
      case 'forms': {
        const { forms, selectedForms } = this.state;
        const transformedForms = forms.map(({ id, ...rest }) => ({
          id,
          ...rest,
          from_group: this.state.formsManagedViaGroup.includes(id),
        }));

        button = {
          type: Resource.Forms,
          label: 'Apps',
          title: transformedForms.length > 0 ? 'Select Apps' : 'No Apps',
          selected: selectedForms.length + inheritedFormIds.length,
          total: transformedForms.length,
          objects: transformedForms,
          selectedIDs: selectedForms,
          preselectedObjects: inheritedForms,
          show: this.state.showFormPermissions,
          disabled: !this.canEditRole || this.isOwnerRole || transformedForms.length === 0,
        };

        break;
      }

      case 'groups': {
        const { groups } = this.state;
        const selected = this.state.selectedGroups;

        button = {
          type: Resource.Groups,
          label: 'Groups',
          title: groups.length > 0 ? 'Select Groups' : 'No Groups',
          selected: this.isOwnerRole ? groups.length : selected.length,
          total: groups.length,
          objects: groups,
          selectedIDs: this.state.selectedGroups,
          show: this.state.showGroupPermissions,
          disabled: !this.canEditRole || this.isOwnerRole || groups.length === 0,
        };

        break;
      }
      default:
        return;
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <Button
          small
          affirm
          style={{ width: 135 }}
          disabled={button.disabled}
          onClick={this.showPermissionModal(button.type)}
          title={button.title}
        >
          {button.title}
        </Button>
        {!!button.total && `${button.selected} selected`}
        <PermissionsEditor
          type={button.type}
          show={button.show}
          objects={button.objects}
          selectedIDs={button.selectedIDs}
          preselectedObjects={button.preselectedObjects}
          onDone={this.handlePermissionsDone(button.type)}
          onCancel={this.handlePermissionsCancel(button.type)}
        />
      </div>
    );
  }

  render() {
    return (
      <Dialog
        showTop
        title="Edit Member Details"
        width={560}
        show={this.props.show}
        positive="Save"
        keyboardDisabled
        positiveDisabled={this.state.hasError || !this.canEditRole || !this.canSubmitMember}
        onPositiveClick={this.handleSubmit}
        onNegativeClick={this.handleCancel}
      >
        {this.state.isLoading
          && <Loader text={this.state.loadingType ? `Processing ${this.state.loadingType}` : undefined} />}
        <ErrorHandler
          isErrored={this.state.hasError}
          errorMsg={this.state.error}
        />
        {this.state.membership && !this.canEditUserProfile && (
          <Alert severity="info">
            {!this.canEditRole && !this.isManagerRoleUser
              ? this.editRoleStatus
              : this.state.isManaged
                ? this.managedStatus
                : !this.state.isEditable
                  ? this.multiOrgStatus
                  : this.isManagerRoleUser
                    ? this.editUserStatus
                    : ''}
          </Alert>
        )}
        <div style={{ opacity: this.state.isLoading ? 0 : 1, ...gridStyle }}>
          <>
            <Label>First Name:</Label>
            <Input
              name="first_name"
              type="text"
              value={this.state.firstName}
              required
              onChange={this.handleFirstNameChange}
              placeholder="Enter a first name"
              disabled={!this.canEditRole || !this.canEditUserProfile}
              onBlur={() => this.setState({ firstName: removeSpaces(this.state.firstName) })}
            />
          </>
          <>
            <Label>Last Name:</Label>
            <Input
              name="last_name"
              type="text"
              value={this.state.lastName}
              required
              onChange={this.handleLastNameChange}
              placeholder="Enter a last name"
              disabled={!this.canEditRole || !this.canEditUserProfile}
              onBlur={() => this.setState({ lastName: removeSpaces(this.state.lastName) })}
            />
          </>
          <>
            <Label>Email:</Label>
            <div style={{ position: 'relative', marginBottom: 0 }}>
              <Input
                name="email"
                type="email"
                value={this.state.email}
                required
                onChange={this.handleEmailChange}
                onBlur={this.handleEmailBlur}
                placeholder="name@example.com"
                disabled={!this.canEditRole || !this.canEditUserProfile}
                style={this.state.emailError ? { borderColor: '#d32f2f', marginBottom: 0 } : { marginBottom: 0 }}
              />
              {this.state.emailError && (
                <span style={{
                  color: '#d32f2f',
                  fontSize: 'inherit',
                  position: 'absolute',
                  top: '100%',
                  left: '0',
                  marginLeft: '8px',
                  whiteSpace: 'nowrap',
                }}
                >
                  {this.state.emailError}
                </span>
              )}
            </div>
          </>
          <>
            <Label>Role:</Label>
            <Select
              styles={styles}
              menuPortalTarget={document.body}
              name="role"
              placeholder={this.canEditRole ? 'Select Role...' : this.state.membership?.role_name}
              isClearable={false}
              isDisabled={!this.canEditRole}
              onChange={this.handleRoleChange}
              options={this.mappedRoles}
              value={this.mappedRoles.filter(
                ({ value, label }) => (
                  value === this.state.role ? label : null
                ),
              )}
            />
          </>
        </div>
        <hr />
        <>
          <div style={{ display: 'flex', marginBottom: '8px' }}>
            <h4>Add to Groups</h4>
          </div>
          <p>{this.groupsText}</p>
          <div style={{ display: 'flex' }}>
            <div>{this.renderPermissions(Resource.Groups)}</div>
          </div>
          <hr />
        </>
        <h4 style={{ marginBottom: '8px' }}>Manage Access</h4>
        <p>{this.individualTextGroups}</p>
        <div style={{ display: 'flex' }}>
          <div>{this.renderPermissions(Resource.Forms)}</div>
          <div>{this.renderPermissions(Resource.Layers)}</div>
          <div>{this.renderPermissions(Resource.Projects)}</div>
        </div>
      </Dialog>
    );
  }
}
